/**
 * Asks once, before a run that holds a video starts, whether its sound is processed too
 * (PRD-0115 SC-001). `ask` resolves with the answer, or null when the officer closed the
 * question: the run then does not start. The dialog that shows it reads `scope` and calls
 * `answer`; one question is open at a time.
 */
import { useCallback, useRef, useState } from 'react'
import type { VideoSoundScope } from '@/components/documents/VideoSoundQuestion'

export interface VideoSoundQuestion {
  /** What is asked about while the question is open, else null. */
  scope: VideoSoundScope | null
  /** Opens the question; resolves with the answer, or null when it was closed unanswered. */
  ask: (scope?: VideoSoundScope) => Promise<boolean | null>
  /** The officer's answer, or null to close the question without one. */
  answer: (sound: boolean | null) => void
}

export function useVideoSoundQuestion(): VideoSoundQuestion {
  const [scope, setScope] = useState<VideoSoundScope | null>(null)
  const pending = useRef<((sound: boolean | null) => void) | null>(null)

  const answer = useCallback((sound: boolean | null) => {
    pending.current?.(sound)
    pending.current = null
    setScope(null)
  }, [])

  const ask = useCallback((next: VideoSoundScope = {}) => {
    // A question still open is closed unanswered: its run does not start.
    pending.current?.(null)
    setScope(next)
    return new Promise<boolean | null>(resolve => {
      pending.current = resolve
    })
  }, [])

  return { scope, ask, answer }
}
